import axios from 'axios';

// Базовый URL API (нужно будет настроить в зависимости от окружения)
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000"; //|| 'https://mako-bot.online/cyber_prime_bot/';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем интерсептор для добавления Telegram WebApp заголовков
api.interceptors.request.use(
  (config) => {
    // Добавляем заголовок с данными Telegram WebApp если доступен
    if (window.Telegram?.WebApp?.initData) {
      config.headers['X-Telegram-WebApp-Data'] = window.Telegram.WebApp.initData;
    }
    
    // Добавляем заголовки для ngrok
    if (API_BASE_URL.includes('ngrok')) {
      config.headers['ngrok-skip-browser-warning'] = 'true';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем интерсептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Обрабатываем новый формат ошибок с нашей базовой схемой
    if (error.response?.data?.success === false || error.response?.data?.error) {
      // Ошибка в нашем формате BaseApiResponse
      const errorMessage = error.response.data.error || 'Произошла ошибка';
      error.message = errorMessage;
    } else if (error.response?.data?.detail?.error) {
      // Ошибка через HTTPException с нашим форматом внутри detail
      const errorMessage = error.response.data.detail.error || 'Произошла ошибка';
      error.message = errorMessage;
    }
    
    return Promise.reject(error);
  }
);

/**
 * Получает текущий актуальный опросник с проверкой на уже пройденные
 * @returns {Promise<Object>} Объект с данными опросника и статусом прохождения
 */
export const fetchCurrentQuiz = async () => {
  try {
    const response = await api.get('/quiz/webapp/current');
    
    // Проверяем формат ответа API
    if (response.data.success === false) {
      throw new Error(response.data.error || 'Ошибка получения опросника');
    }
    
    return response.data.data; // Возвращаем только data из успешного ответа
  } catch (error) {
    // Обрабатываем специфичные статус коды
    switch (error.response?.status) {
      case 401:
        throw new Error('Требуется авторизация через Telegram');
      case 403:
        throw new Error('Нет доступа к опроснику');
      case 404:
        throw new Error('Пользователь не найден');
      case 500:
        throw new Error('Внутренняя ошибка сервера');
      default:
        // Используем сообщение из интерсептора или дефолтное
        throw new Error(error.message || 'Не удалось загрузить опросник');
    }
  }
};

/**
 * Отправляет ответы пользователя на опросник
 * @param {number} quizId - ID опросника
 * @param {number} userId - ID пользователя
 * @param {Object} answers - Ответы в формате {вопрос: ответ}
 * @returns {Promise<Object>} Результат сохранения
 */
export const submitQuizAnswers = async (quizId, userId, answers) => {
  try {
    const response = await api.post('/quiz/webapp/submit', {
      quiz_id: quizId,
      user_id: userId,
      answers: answers,
    });
    
    // Проверяем формат ответа API
    if (response.data.success === false) {
      throw new Error(response.data.error || 'Ошибка сохранения ответов');
    }
    
    return response.data.data; // Возвращаем только data из успешного ответа
  } catch (error) {
    // Обрабатываем специфичные статус коды
    switch (error.response?.status) {
      case 400:
        throw new Error(error.message || 'Некорректные данные опросника');
      case 401:
        throw new Error('Требуется авторизация');
      case 403:
        throw new Error('Нет прав для отправки ответов');
      case 404:
        throw new Error('Опросник не найден');
      case 500:
        throw new Error('Внутренняя ошибка сервера');
      default:
        // Используем сообщение из интерсептора или дефолтное
        throw new Error(error.message || 'Не удалось сохранить ответы');
    }
  }
};

/**
 * Получает опросник по ID
 * @param {number} quizId - ID опросника
 * @returns {Promise<Object>} Данные опросника
 */
export const fetchQuizById = async (quizId) => {
  try {
    const response = await api.get(`/quiz/${quizId}`);
    
    // Проверяем формат ответа API
    if (response.data.success === false) {
      throw new Error(response.data.error || 'Ошибка получения опросника');
    }
    
    return response.data.data;
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.status === 404) {
      throw new Error('Опросник не найден');
    }
    throw new Error(error.message || 'Не удалось загрузить опросник');
  }
};

/**
 * Получает результаты опросников пользователя
 * @param {number} userId - ID пользователя
 * @returns {Promise<Array>} Список результатов
 */
export const fetchUserResults = async (userId) => {
  try {
    const response = await api.get(`/quiz/user/${userId}/results`);
    
    // Проверяем формат ответа API
    if (response.data.success === false) {
      throw new Error(response.data.error || 'Ошибка получения результатов');
    }
    
    return response.data.data;
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error(error.message || 'Не удалось загрузить результаты');
  }
}; 